import React from 'react';
import { countryFlagCode } from '../lib/flags';
import './FlagIcon.css';

// Real flag SVGs for the 6 Gulf destination countries plus Kenya (the origin country, used by
// PhoneInput's +254 prefix) — NOT emoji. Regional-indicator flag emoji
// (🇶🇦 etc.) render as two-letter codes in boxes on Windows (Segoe UI Emoji has never shipped flag
// glyphs) and inconsistently on older Android, so a large share of applicants — on phones, per the
// site's own audience research — never saw a flag at all. These are simplified but recognisable
// at the small size they're actually shown (a jobs-list chip / job-card meta line), not an attempt
// at pixel-perfect vexillology. Saudi Arabia's Shahada is real Arabic text (not a drawn glyph), so
// it renders correctly via the browser's own Arabic-capable system font rather than a hand-traced path.
const FLAGS = {
  qa: (
    <svg viewBox="0 0 30 20" role="img" aria-label="Qatar flag">
      <rect width="30" height="20" fill="#8A1538" />
      <polygon fill="#fff" points="0,0 8,0 5.5,2 8,4 5.5,6 8,8 5.5,10 8,12 5.5,14 8,16 5.5,18 8,20 0,20" />
    </svg>
  ),
  ae: (
    <svg viewBox="0 0 30 20" role="img" aria-label="UAE flag">
      <rect width="30" height="6.67" fill="#00732F" />
      <rect width="30" height="6.67" y="6.67" fill="#fff" />
      <rect width="30" height="6.66" y="13.34" fill="#000" />
      <rect width="9" height="20" fill="#FF0000" />
    </svg>
  ),
  sa: (
    <svg viewBox="0 0 30 20" role="img" aria-label="Saudi Arabia flag">
      <rect width="30" height="20" fill="#006C35" />
      <text
        x="15"
        y="9.5"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#fff"
        fontSize="4.4"
        direction="rtl"
      >
        لا إله إلا الله محمد رسول الله
      </text>
      <rect x="9" y="13.6" width="12" height="1.1" fill="#fff" />
      <polygon points="21,14.15 23.4,13.4 23.4,14.9" fill="#fff" />
    </svg>
  ),
  kw: (
    <svg viewBox="0 0 30 20" role="img" aria-label="Kuwait flag">
      <rect width="30" height="6.67" fill="#007A3D" />
      <rect width="30" height="6.67" y="6.67" fill="#fff" />
      <rect width="30" height="6.66" y="13.34" fill="#CE1126" />
      <polygon points="0,0 0,20 9,10" fill="#000" />
    </svg>
  ),
  bh: (
    <svg viewBox="0 0 30 20" role="img" aria-label="Bahrain flag">
      <rect width="30" height="20" fill="#CE1126" />
      <polygon fill="#fff" points="0,0 8,0 10.86,2 8,4 10.86,6 8,8 10.86,10 8,12 10.86,14 8,16 10.86,18 8,20 0,20" />
    </svg>
  ),
  om: (
    <svg viewBox="0 0 30 20" role="img" aria-label="Oman flag">
      <rect width="30" height="6.67" fill="#fff" />
      <rect width="30" height="6.67" y="6.67" fill="#C8102E" />
      <rect width="30" height="6.66" y="13.34" fill="#008000" />
      <rect width="9" height="20" fill="#C8102E" />
      <rect x="4.1" y="3" width="0.8" height="5.5" fill="#fff" />
      <rect x="2.3" y="7.6" width="4.4" height="0.9" fill="#fff" />
    </svg>
  ),
  // Kenya — origin country for every candidate, not a placement destination (added for
  // PhoneInput's +254 prefix chip, 2026-07-25). Same simplified-but-recognisable approach: the
  // black/red/green bands with white fimbriation already read as Kenya at chip size without the
  // full Maasai shield-and-spears detail.
  ke: (
    <svg viewBox="0 0 30 20" role="img" aria-label="Kenya flag">
      <rect width="30" height="20" fill="#fff" />
      <rect width="30" height="6" fill="#000" />
      <rect width="30" height="1.5" y="6" fill="#C8102E" />
      <rect width="30" height="5" y="7.5" fill="#fff" />
      <rect width="30" height="1.5" y="12.5" fill="#C8102E" />
      <rect width="30" height="6" y="14" fill="#006600" />
      <polygon points="15,7 18.5,8.5 17.5,13.5 15,15 12.5,13.5 11.5,8.5" fill="#C8102E" stroke="#000" strokeWidth="0.6" />
      <polygon points="15,8.3 17,9.3 16.3,12.7 15,13.6 13.7,12.7 13,9.3" fill="#fff" />
    </svg>
  ),
};

export default function FlagIcon({ country, className }) {
  const code = countryFlagCode(country);
  const svg = code && FLAGS[code];
  if (!svg) return null;
  return React.cloneElement(svg, { className: `flag-icon${className ? ` ${className}` : ''}` });
}
